import { FormikProvider, useFormik } from 'formik';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import { ChevronLeftIcon, ChevronRightIcon, LockIcon, WarningIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Button,
	CloseCircleIcon,
	Color,
	LanguageSelect,
	StatusBar,
	Title,
	tokens,
} from '@treams/treams-ui';

import TopBar from '../navigation/TopBar';
import EngagementThankYou from './EngagementThankYou';
import ProgressBar from './ProgressBar';
import Enps from './questions/Enps';
import Face from './questions/Face';
import OpenQuestion from './questions/OpenQuestion';
import PickOne from './questions/PickOne';

const Container = styled(Box)`
	background: ${tokens.surface.secondary};
	min-height: 100vh;
`;

function EngagementQuestion(props: {
	response: graphql.EngagementResponse;
	question: graphql.EngagementQuestion;
	answer?: graphql.EngagementAnswer;
	secret?: string;
}) {
	const history = useHistory();
	const { localize } = useLocalize();
	const [update] = graphql.useUpdateEngagementAnswerMutation();
	const formik = useFormik({
		initialValues: {
			answer: props.answer
				? {
						index: props.answer.index,
						text: props.answer.text,
					}
				: undefined,
		},
		onSubmit: async (values) => {
			await update({
				variables: {
					input: {
						responseID: props.response.id,
						secret: props.secret ?? null,
						questionID: props.question.id,
						skipped: !values.answer,
						index: values.answer?.index,
						text: values.answer?.text,
					},
				},
			});

			history.push(
				'/engagement/' + props.response.id + (props.secret ? '?secret=' + props.secret : ''),
			);
		},
	});

	useEffect(() => {
		const answer = new URLSearchParams(location.search).get('answer') ?? undefined;
		const index = parseInt(answer ?? '');

		if (props.question.type.options && 0 <= index && index < props.question.type.options.length) {
			formik.setFieldValue('answer', {
				index,
				text: undefined,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Flex flexDirection="column" alignItems="center" flexGrow={1} maxWidth={670}>
			<Title textAlign="center" mb={4}>
				{localize(props.question.title).text}
			</Title>
			<FormikProvider value={formik}>
				{props.question.type.type === graphql.EngagementResponseType.PickOne && (
					<PickOne type={props.question.type} />
				)}
				{props.question.type.type === graphql.EngagementResponseType.Face && (
					<Face type={props.question.type} />
				)}
				{props.question.type.type === graphql.EngagementResponseType.Open && (
					<OpenQuestion type={props.question.type} />
				)}
				{props.question.type.type === graphql.EngagementResponseType.Enps && (
					<Enps type={props.question.type} />
				)}
			</FormikProvider>
		</Flex>
	);
}

function EngagementResponseRedirect(props: {
	response: graphql.EngagementResponse;
	secret?: string;
}) {
	const question = props.response.request.questions.find(
		(question) => !props.response.answers.some((answer) => question.id === answer.questionID),
	);

	if (!question) {
		return <EngagementThankYou endPage={props.response.request.template?.endPage ?? undefined} />;
	}

	return (
		<Redirect
			to={`/engagement/${props.response.id}/${question.id}${
				props.secret ? '?secret=' + props.secret : ''
			}`}
		/>
	);
}

function EngagementNavigationButtons(props: {
	responseID: string;
	questionID: string;
	previousQuestionID?: string;
	secret?: string;
}) {
	const { t } = useTranslation();
	const history = useHistory();
	const [update] = graphql.useUpdateEngagementAnswerMutation();

	return (
		<Flex width="100%" justifyContent="flex-end" mb={3}>
			{props.previousQuestionID && (
				<Box flexGrow={0} flexShrink={0}>
					<Button
						title={t('engagement.respond.back')}
						type="secondary"
						small
						icon={<ChevronLeftIcon />}
						onClick={() => {
							history.push(
								'/engagement/' +
									props.responseID +
									'/' +
									props.previousQuestionID! +
									(props.secret ? '?secret=' + props.secret : ''),
							);
						}}
					/>
				</Box>
			)}
			<Box flexGrow={1} flexShrink={1} />
			<Box flexGrow={0} flexShrink={0}>
				<Color color="slateBlue">
					<Button
						title={t('engagement.respond.skip')}
						type="plain"
						small
						rightIcon={<ChevronRightIcon />}
						onClick={async () => {
							await update({
								variables: {
									input: {
										responseID: props.responseID,
										secret: props.secret ?? null,
										questionID: props.questionID,
										skipped: true,
										index: undefined,
										text: undefined,
									},
								},
							});

							history.push(
								'/engagement/' + props.responseID + (props.secret ? '?secret=' + props.secret : ''),
							);
						}}
					/>
				</Color>
			</Box>
		</Flex>
	);
}

export default function EngagementResponse(props: { id: string; secret?: string }) {
	const { t } = useTranslation();

	const engagementQuery = graphql.useEngagementResponseQuery({
		variables: {
			id: props.id,
			secret: props.secret ?? null,
		},
	});

	if (!engagementQuery.data) {
		return null;
	}

	const engagementResponse = engagementQuery.data.engagementResponse;

	const isAnonymous =
		engagementResponse.request.template?.anonymityThreshold &&
		engagementResponse.request.template.anonymityThreshold > 1;

	return (
		<Container>
			{isAnonymous ? (
				<StatusBar
					icon={<LockIcon size={16} />}
					text={
						<>
							{t('engagement.respond.anonymous.title.0')}{' '}
							<strong>{t('engagement.respond.anonymous.title.1')}</strong>.
						</>
					}
					end={<LanguageSelect.Synced />}
				/>
			) : (
				<StatusBar
					icon={<WarningIcon size={16} />}
					text={<>{t('engagement.respond.notAnonymous.title.0')}</>}
					end={<LanguageSelect.Synced />}
				/>
			)}
			<TopBar plain />
			<Flex p={3} flexDirection="column" alignItems="center">
				{engagementResponse.isExpired && (
					<Flex flexDirection="column" alignItems="center" flexGrow={1} maxWidth={670} mt={5}>
						<Box mt={2} mb={4}>
							<CloseCircleIcon size={56} color={tokens.icon.secondary} aria-hidden />
						</Box>
						<Title textAlign="center" mb={4}>
							{t('engagement.respond.expired')}
						</Title>
					</Flex>
				)}
				{!engagementResponse.isExpired && (
					<Switch>
						<Route path={`/engagement/:id`} exact>
							<EngagementResponseRedirect
								response={engagementResponse as graphql.EngagementResponse}
								secret={props.secret}
							/>
						</Route>
						<Route path={`/engagement/:id/:question`} exact>
							{({ match }) => (
								<>
									<EngagementNavigationButtons
										responseID={engagementResponse.id}
										secret={props.secret}
										questionID={match!.params.question!}
										previousQuestionID={
											engagementResponse.request.questions[
												engagementResponse.request.questions.findIndex(
													(question) => question.id === match!.params.question,
												) - 1
											]?.id
										}
									/>
									<Flex mt={0} width="100%" justifyContent="center">
										<EngagementQuestion
											key={match!.params.question}
											response={engagementResponse as graphql.EngagementResponse}
											secret={props.secret}
											question={
												engagementResponse.request.questions.find(
													(question) => question.id === match!.params.question,
												) as graphql.EngagementQuestion
											}
											answer={
												engagementResponse.answers.find(
													(answer) => answer.questionID === match!.params.question,
												) as graphql.EngagementAnswer
											}
										/>
									</Flex>
									<Box mt={5} mb={5}>
										<ProgressBar
											selected={match!.params.question}
											response={engagementResponse as graphql.EngagementResponse}
											secret={props.secret}
										/>
									</Box>
								</>
							)}
						</Route>
					</Switch>
				)}
			</Flex>
		</Container>
	);
}
